import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
    FileText, 
    MapPin, 
    Calendar, 
    ArrowLeft, 
    ArrowRight,
    Loader2,
    ShieldCheck,
    Filter
} from 'lucide-react';
import { reviewAPI } from '../lib/api';
import { formatDate, formatDateShort } from '../lib/utils';

const ReviewHistory = () => {
    const [loading, setLoading] = useState(true);
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [reviewsRes, statsRes] = await Promise.all([
                reviewAPI.getMyReviews(),
                reviewAPI.getMyStats()
            ]);
            setReviews(reviewsRes.data || []);
            setStats(statsRes.data);
        } catch (err) {
            console.error('Failed to load review history:', err);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            approved: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
            pending: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
            hidden: 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
            flagged: 'bg-red-500/15 text-red-600 dark:text-red-400',
            rejected: 'bg-red-500/15 text-red-600 dark:text-red-400'
        };
        const labels = {
            approved: 'Active',
            pending: 'Pending',
            hidden: 'Hidden',
            flagged: 'Flagged',
            rejected: 'Rejected'
        };
        return (
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${styles[status] || styles.pending}`}>
                {labels[status] || status}
            </span>
        );
    };

    const filteredReviews = reviews.filter(review => {
        if (filter === 'all') return true;
        if (filter === 'active') return review.status === 'approved';
        if (filter === 'hidden') return review.status === 'hidden';
        if (filter === 'pending') return review.status === 'pending' || review.status === 'flagged';
        return true;
    });

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <Link to="/profile">
                        <Button variant="ghost" size="icon" className="h-9 w-9">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                    </Link>
                    <div>
                        <h1 className="font-heading font-bold text-2xl text-foreground">
                            Your Review History
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            All reviews you've submitted on ClientCheck
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <>
                        {/* Stats Cards */}
                        {stats && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                                <div className="p-4 bg-card rounded-lg border border-border text-center">
                                    <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                                    <p className="text-xs text-muted-foreground">Total Submitted</p>
                                </div>
                                <div className="p-4 bg-card rounded-lg border border-border text-center">
                                    <p className="text-2xl font-bold text-emerald-500">{stats.approved}</p>
                                    <p className="text-xs text-muted-foreground">Active</p>
                                </div>
                                <div className="p-4 bg-card rounded-lg border border-border text-center">
                                    <p className="text-2xl font-bold text-amber-500">{stats.pending}</p>
                                    <p className="text-xs text-muted-foreground">Pending</p>
                                </div>
                                <div className="p-4 bg-card rounded-lg border border-border text-center">
                                    <p className="text-2xl font-bold text-primary">{stats.verified_count}</p>
                                    <p className="text-xs text-muted-foreground">Verified</p>
                                </div>
                            </div>
                        )}

                        {/* Filter Tabs */}
                        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
                            <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            {['all', 'active', 'pending', 'hidden'].map((f) => (
                                <button
                                    key={f}
                                    onClick={() => setFilter(f)}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                                        filter === f
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-accent/50 text-muted-foreground hover:text-foreground'
                                    }`}
                                >
                                    {f.charAt(0).toUpperCase() + f.slice(1)}
                                    {f === 'all' && ` (${reviews.length})`}
                                </button>
                            ))}
                        </div>

                        {/* Reviews List */}
                        {filteredReviews.length > 0 ? (
                            <div className="space-y-3">
                                {filteredReviews.map((review) => (
                                    <Card key={review.id} className="bg-card border-border">
                                        <CardContent className="p-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                    {/* Address */}
                                                    <div className="flex items-start gap-2 mb-2">
                                                        <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-sm text-foreground truncate">
                                                                {review.address_street || 'Address'}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground truncate">
                                                                {review.address_city}{review.address_state && `, ${review.address_state}`}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Job Info */}
                                                    <div className="flex flex-wrap items-center gap-2 text-xs">
                                                        <span className="px-2 py-0.5 bg-accent/50 rounded text-foreground">
                                                            {review.job_type}
                                                        </span>
                                                        <span className="text-muted-foreground flex items-center gap-1">
                                                            <Calendar className="h-3 w-3" />
                                                            {formatDateShort(review.created_at)}
                                                        </span>
                                                        {getStatusBadge(review.status)}
                                                        {review.has_verified_evidence && (
                                                            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                                                <ShieldCheck className="h-3 w-3" />
                                                                Verified
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Notes Preview */}
                                                    {review.notes && (
                                                        <p className="text-xs text-muted-foreground mt-2 line-clamp-1 italic">
                                                            "{review.notes}"
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Action */}
                                                <Link to={`/address/${review.address_id}`} className="flex-shrink-0">
                                                    <Button variant="outline" size="sm" className="w-full sm:w-auto">
                                                        View Address Profile
                                                        <ArrowRight className="h-4 w-4 ml-1" />
                                                    </Button>
                                                </Link>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground">
                                    {filter === 'all' 
                                        ? "You haven't submitted any reviews yet"
                                        : `No ${filter} reviews found`}
                                </p>
                                {filter === 'all' && (
                                    <Link to="/">
                                        <Button className="mt-4">
                                            Search an Address
                                        </Button>
                                    </Link>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
};

export default ReviewHistory;
